import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card.tsx';
import { Button } from '../ui/button.tsx';
import { Input } from '../ui/input.tsx';
import { Label } from '../ui/label.tsx';
import { Badge } from '../ui/badge.tsx';
import { FolderPlus, Upload, ImageIcon as ImageIconLucide, Trash2 } from 'lucide-react';

interface AdminGalleryFolder {
    id: number;
    name: string;
    visibility: 'public' | 'registered';
    created_at: string;
}

interface GalleryTabProps {
    galleryFolders: AdminGalleryFolder[];
    newAlbum: {
        title: string;
        visibility: string;
        category: string;
    };
    setNewAlbum: (value: any) => void;
    newImage: {
        albumId: string;
        title: string;
    };
    setNewImage: (value: any) => void;
    selectedGalleryFiles: FileList | null;
    handleGalleryFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleCreateAlbum: (e: React.FormEvent) => Promise<void>;
    handleDeleteAlbum: (id: number) => Promise<void>;
    handleUploadImage: (e: React.FormEvent) => Promise<void>;
    loading: boolean;
}

export function GalleryTab({
    galleryFolders,
    newAlbum,
    setNewAlbum,
    newImage,
    setNewImage,
    selectedGalleryFiles,
    handleGalleryFileChange,
    handleCreateAlbum,
    handleDeleteAlbum,
    handleUploadImage,
    loading
}: GalleryTabProps) {
    return (
        <div className="space-y-6">
            {/* 1) Albüm Oluşturma */}
            <Card>
                <CardHeader>
                    <CardTitle>Galeri Albümleri</CardTitle>
                    <CardDescription>
                        Yeni albüm oluşturabilir, görünürlüğünü seçebilirsin.
                    </CardDescription>
                </CardHeader>

                <CardContent className="space-y-6">
                    {/* Albüm Form */}
                    <div className="bg-slate-50 p-4 rounded-lg border">
                        <h4 className="font-semibold mb-3 flex items-center gap-2">
                            <FolderPlus className="h-4 w-4" />
                            Yeni Albüm Oluştur
                        </h4>

                        <form
                            onSubmit={handleCreateAlbum}
                            className="grid md:grid-cols-4 gap-4 items-end"
                        >
                            {/* Albüm Adı */}
                            <div className="space-y-1 md:col-span-2">
                                <Label className="text-xs">Albüm Adı *</Label>
                                <Input
                                    placeholder="Örn: 2024 Mezuniyet"
                                    value={newAlbum.title}
                                    onChange={(e) =>
                                        setNewAlbum({ ...newAlbum, title: e.target.value })
                                    }
                                    required
                                />
                            </div>

                            {/* Visibility */}
                            <div className="space-y-1">
                                <Label className="text-xs">Görünürlük</Label>
                                <select
                                    className="w-full h-10 px-3 rounded-md border"
                                    value={newAlbum.visibility}
                                    onChange={(e) =>
                                        setNewAlbum({
                                            ...newAlbum,
                                            visibility: e.target.value as any,
                                        })
                                    }
                                >
                                    <option value="registered">Sadece Üyeler</option>
                                    <option value="public">Herkese Açık</option>
                                </select>
                            </div>

                            {/* Submit */}
                            <Button type="submit" className="bg-orange-600 text-white">
                                Oluştur
                            </Button>
                        </form>
                    </div>

                    {/* Albüm Listesi */}
                    <div className="grid md:grid-cols-3 gap-4">
                        {galleryFolders.map((folder) => (
                            <div
                                key={folder.id}
                                className="border rounded-lg p-4 flex flex-col justify-between bg-white hover:shadow-md transition"
                            >
                                <div>
                                    <div className="flex justify-between items-start mb-2">
                                        <ImageIconLucide className="h-7 w-7 text-orange-500" />
                                        <Badge variant="secondary" className="text-[10px]">
                                            {folder.visibility === "public"
                                                ? "Herkese Açık"
                                                : "Üyelere Özel"}
                                        </Badge>
                                    </div>

                                    <h3 className="font-bold text-lg">{folder.name}</h3>
                                    <p className="text-xs text-slate-500 mt-1">
                                        {new Date(folder.created_at).toLocaleDateString("tr-TR")}
                                    </p>
                                </div>

                                <div className="mt-4 pt-3 border-t flex justify-end">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="text-red-500 hover:bg-red-50"
                                        onClick={() => handleDeleteAlbum(folder.id)}
                                    >
                                        <Trash2 className="h-4 w-4 mr-1" />
                                        Sil
                                    </Button>
                                </div>
                            </div>
                        ))}

                        {galleryFolders.length === 0 && (
                            <p className="text-sm text-slate-500 col-span-3 text-center py-4">
                                Henüz albüm oluşturulmamış.
                            </p>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* 2) Fotoğraf Yükleme */}
            <Card>
                <CardHeader>
                    <CardTitle>Fotoğraf Yükle</CardTitle>
                    <CardDescription>
                        Albüm seçerek veya seçmeden (Genel Galeri) çoklu fotoğraf
                        yükleyebilirsin.
                    </CardDescription>
                </CardHeader>

                <CardContent>
                    <div className="bg-slate-50 p-6 rounded-lg border space-y-4">
                        <form onSubmit={handleUploadImage} className="space-y-4">
                            {/* Albüm Seç */}
                            <div className="space-y-2">
                                <Label>Albüm (Opsiyonel)</Label>

                                <select
                                    className="w-full h-10 px-3 rounded-md border"
                                    value={newImage.albumId}
                                    onChange={(e) =>
                                        setNewImage({ ...newImage, albumId: e.target.value })
                                    }
                                >
                                    <option value="">
                                        Genel Galeri (Albümsüz - Public)
                                    </option>

                                    {galleryFolders.map((f) => (
                                        <option key={f.id} value={f.id}>
                                            {f.name}
                                        </option>
                                    ))}
                                </select>

                                <p className="text-[11px] text-slate-500">
                                    Albüm seçmezsen fotoğraf ana galeride herkese açık görünür.
                                </p>
                            </div>

                            {/* Caption */}
                            <div className="space-y-2">
                                <Label>Fotoğraf Açıklaması (Opsiyonel)</Label>
                                <Input
                                    placeholder="Örn: Mezuniyet Töreni"
                                    value={newImage.title}
                                    onChange={(e) =>
                                        setNewImage({ ...newImage, title: e.target.value })
                                    }
                                />
                            </div>

                            {/* Dosya Seç */}
                            <div className="space-y-2">
                                <Label>Fotoğrafları Seç (Çoklu)</Label>

                                <Input
                                    id="gallery-file-input"
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    onChange={handleGalleryFileChange}
                                    required
                                />

                                {selectedGalleryFiles && (
                                    <p className="text-xs text-green-600 font-medium">
                                        {selectedGalleryFiles.length} adet dosya seçildi ✅
                                    </p>
                                )}
                            </div>

                            {/* Upload Button */}
                            <Button
                                type="submit"
                                disabled={loading}
                                className="w-full bg-blue-600 text-white"
                            >
                                {loading ? (
                                    "Yükleniyor..."
                                ) : (
                                    <>
                                        <Upload className="h-4 w-4 mr-2" />
                                        Fotoğrafları Yükle
                                    </>
                                )}
                            </Button>
                        </form>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
